import { assertMessageEvent, ControllerMessageType, WorkerMessageType } from "../../shared/Messages.js";
import { isTransferDescriptor } from "../../shared/TransferDescriptor.js";
import { assert, getRandomUID, withTimeout } from "../../shared/Utils.js";
import { EsTaskPromise } from "./EsTask.js";
import EventTarget from "@ungap/event-target";
const DefaultEsThreadOptions = {
    timeout: 10000
};
export class EsThread extends EventTarget {
    threadUID = getRandomUID();
    options;
    tasks = new Map();
    worker;
    interface;
    methods = {};
    get numQueuedTasks() { return this.tasks.size; }
    constructor(worker, threadOptions) {
        super();
        this.options = { ...DefaultEsThreadOptions, ...threadOptions };
        if (typeof ServiceWorker !== "undefined" && worker instanceof ServiceWorker) {
            throw new Error("ServiceWorker currently not supported.");
        }
        this.worker = worker;
        if (worker instanceof Worker)
            this.interface = worker;
        else {
            assert(worker instanceof SharedWorker);
            this.interface = worker.port;
            worker.port.start();
        }
    }
    static async Spawn(worker, threadOptions = {}) {
        const thread = new EsThread(worker, threadOptions);
        return thread.initThread();
    }
    async settled() {
        await Promise.allSettled(this.tasks.values());
    }
    async resolved() {
        await Promise.all(this.tasks.values());
    }
    async terminate(forceTerminateShared) {
        await this.settled();
        const terminateMessage = {
            type: ControllerMessageType.Terminate,
            forceTerminateShared: forceTerminateShared
        };
        this.interface.postMessage(terminateMessage, []);
        this.interface.removeEventListener("message", this.taskResultDispatch);
        if (this.worker instanceof Worker)
            this.worker.terminate();
        else {
            assert(this.worker instanceof SharedWorker);
            this.worker.port.close();
        }
    }
    taskResultDispatch = (evt) => {
        try {
            assertMessageEvent(evt);
            switch (evt.data.type) {
                case WorkerMessageType.TaskResult:
                    {
                        const task = this.tasks.get(evt.data.uid);
                        if (!task)
                            throw new Error("Recived result for invalid task with UID " + evt.data.uid);
                        this.tasks.delete(task.taskUID);
                        task.resolve(evt.data.result);
                    }
                    break;
                case WorkerMessageType.TaskError:
                    {
                        const task = this.tasks.get(evt.data.uid);
                        if (!task)
                            throw new Error("Recived error for invalid task with UID " + evt.data.uid);
                        this.tasks.delete(task.taskUID);
                        task.reject(new Error(evt.data.errorMessage));
                    }
                    break;
                case WorkerMessageType.UnchaughtError:
                    throw new Error("Uncaught error in worker: " + evt.data.errorMessage);
                default:
                    throw new Error("Recieved unexpected WorkerMessage of type: " + evt.data.type);
            }
        }
        catch (e) {
            this.dispatchEvent(new ErrorEvent("error", { error: e }));
        }
    };
    static prepareArguments(rawArgs) {
        const args = [];
        const transferables = [];
        for (const arg of rawArgs) {
            if (isTransferDescriptor(arg)) {
                transferables.push(...arg.transferables);
                args.push(arg);
            }
            else {
                args.push(arg);
            }
        }
        return { args: args, transferables: transferables };
    }
    createProxyFunction(method) {
        return ((...rawArgs) => {
            const taskPromise = new EsTaskPromise();
            const { args, transferables } = EsThread.prepareArguments(rawArgs);
            const runMessage = {
                type: ControllerMessageType.Run,
                uid: taskPromise.taskUID,
                method: method,
                args: args
            };
            this.tasks.set(taskPromise.taskUID, taskPromise);
            this.interface.postMessage(runMessage, transferables);
            return taskPromise;
        });
    }
    createMethodsProxy(methodNames) {
        const proxy = this.methods;
        for (const methodName of methodNames) {
            proxy[methodName] = this.createProxyFunction(methodName);
        }
    }
    async initThread() {
        let exposedApi;
        try {
            exposedApi = await withTimeout(new Promise((resolve, reject) => {
                const initMessageHandler = (event) => {
                    assertMessageEvent(event);
                    switch (event.data.type) {
                        case WorkerMessageType.Init:
                            this.interface.removeEventListener("message", initMessageHandler);
                            resolve(event.data);
                            break;
                        case WorkerMessageType.UnchaughtError:
                            this.interface.removeEventListener("message", initMessageHandler);
                            reject(new Error(event.data.errorMessage));
                            break;
                        default:
                            this.interface.removeEventListener("message", initMessageHandler);
                            reject(new Error("Recieved unexpected WorkerMessage of type: " + event.data.type));
                    }
                };
                this.interface.addEventListener("message", initMessageHandler);
            }), this.options.timeout, `Timeout: Did not receive an init message from worker after ${this.options.timeout}ms`);
        }
        catch (e) {
            if (this.worker instanceof Worker)
                this.worker.terminate();
            else {
                assert(this.worker instanceof SharedWorker);
                this.worker.port.close();
            }
            throw e;
        }
        this.createMethodsProxy(exposedApi.methodNames);
        this.interface.addEventListener("message", this.taskResultDispatch);
        return this;
    }
}
